import { useEffect, useState } from "react";
import api from "../../../api/axiosInstance";
import Icon from "../../../components/AppIcon";

const emptyPlan = {
  name: "",
  description: "",
  planType: "free",
  icon: "credit-card",
  ctaText: "",
  monthlyPrice: "",
  annualPrice: "",
  trialDays: 0,
  isPopular: false,
  features: [],
  included: true,
  isActive: true
};

const PlanAdmin = () => {
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState(emptyPlan);
  const [editingId, setEditingId] = useState(null);

  const handleInputChange = (key, value) => {
    setForm(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const load = async () => {
    const res = await api.get("/api/plans/admin");
    setPlans(res.data || []);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    console.log("Save function called");
    console.log("Current form data:", form);
    console.log("Editing ID:", editingId);
    
    editingId
      ? await api.post(`/api/plans/${editingId}`, form)
      : await api.post("/api/plans/", form);

    setForm(emptyPlan);
    setEditingId(null);
    load();
  };

  const edit = (p) => {
    setEditingId(p._id);
    setForm(p);
  };

 const deactivate = async (id) => {
  console.log("DEACTIVATING:", id);

  if (!window.confirm("Deactivate this plan?")) return;

  try {
    await api.delete(`/api/plans/${id}`);
    load();
  } catch (err) {
    console.error("Deactivate error:", err.response?.data);
  }
};

  const addFeature = () => {
    setForm({
      ...form,
      features: [...form.features, { text: "", included: true }]
    });
  };

  const updateFeature = (i, key, value) => {
    const features = [...form.features];
    features[i][key] = value;
    setForm({ ...form, features });
  };
  const activate = async (id) => {
    if (!window.confirm("Activate this plan?")) return;

    try {
      await api.patch(`/api/plans/${id}/activate`);
      load();
    } catch (err) {
      console.error("Activate failed:", err.response?.data);
    }
  };

  const removeFeature = (i) => {
    setForm({
      ...form,
      features: form.features.filter((_, idx) => idx !== i)
    });
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* HEADER */}
      <div>
        <h1 className="text-3xl font-bold">Pricing Plans</h1>
        <p className="text-gray-500 text-sm">
          Create and manage subscription plans
        </p>
      </div>

      <div className="grid lg:grid-cols-4 gap-8">
        {/* FORM */}
        <div className="lg:col-span-1">
          <div className="bg-white border rounded-2xl p-5 space-y-4">
            <h2 className="font-semibold">
              {editingId ? "Edit Plan" : "New Plan"}
            </h2>

            {[
              ["Plan Name", "name"],
              ["Description", "description"],
              ["Icon (lucide)", "icon"],
              ["CTA Text", "ctaText"]
            ].map(([label, key]) => (
              <div key={key}>
                <label className="text-xs text-gray-500">{label}</label>
                <input
                  className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                  value={form[key]}
                  onChange={(e) =>
                    setForm({ ...form, [key]: e.target.value })
                  }
                />
              </div>
            ))}

            {/* PRICE */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500">
                  Monthly ($)
                </label>
                <input
                  className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                  value={form.monthlyPrice}
                  onChange={(e) => {
                    const value = e.target.value;
                    console.log(`Price input change - key: monthlyPrice, value: ${value}, type: ${typeof value}`);
                    setForm({ ...form, monthlyPrice: Number(value) || 0 })
                  }}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">
                  Annual ($)
                </label>
                <input
                  className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                  value={form.annualPrice}
                  onChange={(e) =>
                    setForm({ ...form, annualPrice: e.target.value })
                  }
                />
              </div>
            </div>

            <label className="flex gap-2 items-center text-sm">
              <input
                type="checkbox"
                checked={form.isPopular}
                onChange={(e) =>
                  setForm({ ...form, isPopular: e.target.checked })
                }
              />
              Mark as popular
            </label>

            {/* FEATURES */}
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Features</span>
                <button
                  onClick={addFeature}
                  className="text-xs text-blue-600"
                >
                  + Add
                </button>
              </div>

              {form.features.map((f, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2"
                >
                  <input
                    className="flex-1 border rounded-lg px-2 py-1 text-xs"
                    placeholder="Feature description"
                    value={f.text}
                    onChange={(e) =>
                      updateFeature(i, "text", e.target.value)
                    }
                  />
                  <input
                    type="checkbox"
                    checked={f.included}
                    onChange={(e) =>
                      updateFeature(i, "included", e.target.checked)
                    }
                  />
                  <button
                    onClick={() => removeFeature(i)}
                    className="text-xs text-red-500"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            <button
              onClick={save}
              className="w-full bg-blue-600 text-white py-2 rounded-xl text-sm font-medium"
            >
              {editingId ? "Update Plan" : "Create Plan"}
            </button>
          </div>
        </div>

        {/* PLANS */}
        <div className="lg:col-span-3">
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
            {plans.map((p) => (
              <div
                key={p._id}
                className={`relative border rounded-2xl p-6 bg-white ${
                  p.isPopular ? "ring-2 ring-blue-600" : ""
                }`}
              >
                {p.isPopular && (
                  <span className="absolute -top-3 left-4 bg-blue-600 text-white text-xs px-3 py-1 rounded-full">
                    Popular
                  </span>
                )}

                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Icon name={p.icon || "credit-card"} />
                    <h3 className="font-semibold">{p.name}</h3>
                  </div>

                  <p className="text-sm text-gray-500">
                    {p.description}
                  </p>

                  <div>
                    <span className="text-3xl font-bold">
                      ${p.monthlyPrice}
                    </span>
                    <span className="text-sm text-gray-500">
                      /month
                    </span>
                  </div>

                  <ul className="space-y-2 text-sm">
                    {p.features?.map((f, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-2"
                      >
                        <span
                          className={
                            f.included
                              ? "text-green-600"
                              : "text-gray-300"
                          }
                        >
                          ✓
                        </span>
                        <span
                          className={
                            f.included
                              ? ""
                              : "line-through text-gray-400"
                          }
                        >
                          {f.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex items-center justify-between pt-6 text-sm">
  {/* Left actions */}
  <div className="flex gap-4">
    <button
      onClick={() => edit(p)}
      className="text-blue-600"
    >
      Edit
    </button>

    {p.isActive ? (
      <button
        onClick={() => deactivate(p._id)}
        className="text-red-600"
      >
        Deactivate
      </button>
    ) : (
      <button
        onClick={() => activate(p._id)}
        className="text-green-600"
      >
        Activate
      </button>
    )}
  </div>

  {/* Status badge */}
  <span
    className={`text-xs px-2 py-1 rounded-full ${
      p.isActive
        ? "bg-green-100 text-green-700"
        : "bg-gray-200 text-gray-500"
    }`}
  >
    {p.isActive ? "Active" : "Inactive"}
  </span>
</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanAdmin;
